import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { BillingBannerStatus, BillingState } from '../types'

export const billingQueryKeys = {
  all: ['billing'] as const,
  state: ['billing', 'state'] as const,
  status: ['billing', 'status'] as const,
}

/**
 * The banner's state. Open to every signed-in user, so staff learn why a
 * write was refused. Rechecked every few minutes so a trial that ends while
 * the tab is open shows up without a reload.
 */
export function useBillingStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: billingQueryKeys.status,
    queryFn: async (): Promise<BillingBannerStatus> => {
      const { data } =
        await apiClient.get<ApiResponseWrapper<BillingBannerStatus>>('/billing/status')
      return data.data
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  })
}

export function useBilling(options?: { pollMs?: number; enabled?: boolean }) {
  return useQuery({
    queryKey: billingQueryKeys.state,
    queryFn: async (): Promise<BillingState> => {
      const { data } = await apiClient.get<ApiResponseWrapper<BillingState>>('/billing')
      return data.data
    },
    // The return-from-Checkout page polls: the browser comes back before the
    // webhook lands, so the only honest way to know is to keep asking.
    refetchInterval: options?.pollMs,
    staleTime: options?.pollMs ? 0 : 30_000,
    enabled: options?.enabled !== false,
  })
}

/**
 * Sends a plan key and nothing else — never a price or an amount. The server
 * resolves what to charge, so there is no number here worth tampering with.
 */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (plan: string): Promise<string> => {
      const { data } = await apiClient.post<ApiResponseWrapper<{ url: string }>>(
        '/billing/checkout',
        { plan }
      )
      return data.data.url
    },
    onSuccess: (url) => {
      // A full navigation, not a new tab: Checkout is the next step of this
      // task, and a popup would be blocked as often as not.
      window.location.href = url
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** Stripe's own portal for changing card, switching plan or cancelling. */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const { data } = await apiClient.post<ApiResponseWrapper<{ url: string }>>('/billing/portal')
      return data.data.url
    },
    onSuccess: (url) => {
      window.location.href = url
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRefreshBilling() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: billingQueryKeys.all })
}
